// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

// see https://stackoverflow.com/questions/30774874/enum-as-parameter-in-typescript
export type EnumTypeString<TEnum extends string> = { [key in string]: TEnum | string };

export enum RootPath {
  RFID = "rfid",
  TREND = "trend",
  OCPP = "ocpp",
}

export enum MainPanel {
  LOCAL_SETTINGS = "localsettings",
  SITES = "sites",
  SITE_INFO = "siteinfo", // details of the connected site
  SITE_SETTINGS = "sitesettings",
  SITE_PARAMETERS = "siteparameters",
  USER = "user", // user information page
  USER_SETTINGS = "usersettings", // user administration/settings page
  DASHBOARD = "dashboard",
  MONITOR = "monitor",
  POWER = "power",
  ENERGYMETERS = "energymeters",
  ENERGY = "energy",
  TRENDMONITOR = "energymonitor",
  REPORT = "report",
  REPORTDATAVIEW = "reportdataview",
  IOS = "ios",
  IO_TREND = "iotrend",
  CHARGERLIST = "chargerlist",
  CHARGERDETAIL = "chargerdetail",
  RFIDLIST = "cardlist",
  RFIDDETAIL = "carddetail",
  ENLINE_CHARGER = "enlinecharger",
  OCPPCONFIGURATION = "chargingconfig",
  CHARGINGSESSIONLIST = "chargingsessionlist",
  CHARGINGSESSION = "chargingsession",
  ALARMS = "alarms",
  MODULES = "modules",
  CONDITIONDETAIL = "conditiondetail",
  CUSTOM_DASHBOARD = "customdashboard", // same as public display, but without navigation headers
  PUBLIC_DISPLAY = "publicdisplay", // same as custom dashboard, but with navigatin headers
}

// available panels on dashboard screen
export enum DashboardPanel {
  ALARMS = "Alarms",
  EMOBILTY = "EMobility",
  ENERGY = "Energy",
  INPUTS = "Inputs",
  MONITOR = "Monitor",
  OUTPUTS = "Outputs",
  POWER = "Power",
  REPORTS = "Reports",
  SITEINFOS = "SiteInfos",
  TRENDMONITOR = "TrendMonitor",
  EMPTY = "empty",
}

export enum ForegroundPanel {
  SIGNUP = "signup",
  SIGNUP_CONFIRM = "signupconfirm",
  FORGOT_PASSWORD = "forgotpassword",
  SITESELECTION = "siteselection",
  PASSWORD_CHANGE = "passwordChange",
  PASSWORD_CONFIRM = "passwordConfirm",
  MFA_CODE = "mfaCode",
}

// the DataState enum is ordered, such that later state is "more important" than former states
export enum DataState {
  EMPTY,
  CLEAN,
  SAVE_SUCC,
  DIRTY,
  SAVE_REQ,
  SAVE_FAIL,
}

export type PeriodIdentfier = "trendPeriod" | "ioPeriod" | "chargingSessionFilter";

export interface ICloudTrendMember {
  regperiod: number;
  energy: number;
  power: number;
  costs?: number | null;
  tariff?: number | null;
  targetpower?: number | null;
  soc?: number | null;
}

export enum TimePeriod {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
  YEAR = "year",
  ALL = "all",
  CUSTOM = "custom",
}

export interface GraphQLResult<T = object> {
  data: T;
  errors?: Error[]; // actually GraphQLError, but this extends Error
  extensions?: {
    [key: string]: any;
  };
}

export enum UserStateAWSError {
  UNKNOWN = 0, // unknown state at AWS (should never happen)
  SIGNUP_USER_EXISTS,
  SIGNUP_UNSUPPORTED_COMPANY,
  CONFIRMATION_PENDING,
  CONFIRMATION_FAILED,
  USERNAME_MISSING,
  PASSWORD_MISSING,
  CODE_MISSING,
  CODE_EXPIRED,
  CODE_MISMATCH,
  PASSWORD_INVALID, // password does not meet requirements
  ALREADY_CONFIRMED,
  PASSWORD_CHANGE_FAIL,
  LOGIN_FAILED,
  LOGOUT_FAILED,
}

export enum Theme {
  EMBYT,
  EPM,
  EATON,
}

export interface INavigationPanelEntry {
  heading: MainPanel;
  childPanels?: INavigationPanelEntry[];
  subPanels?: MainPanel[];
}

export interface IDashboardEntry {
  entry: DashboardPanel;
  prio: number; // lower prio is shown first
}
